import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Flight } from "@/types/flight";
import { SEOHead } from "@/components/SEOHead";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { FIDSHeader } from "@/components/fids/FIDSHeader";
import { FIDSFlightBoard } from "@/components/fids/FIDSFlightBoard";
import { FIDSMarquee } from "@/components/fids/FIDSMarquee";
import { FlightDetailPanel } from "@/components/fids/FlightDetailPanel";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { OrganizationLinks } from "@/components/OrganizationLinks";

export default function OrganizationFlightsPage() {
  const { orgCode } = useParams<{ orgCode: string }>();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const slug = orgCode?.toLowerCase() || "";

  // 1. Resolve org by code
  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ["org-by-code", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .ilike("code", slug)
        .eq("is_active", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // 2. Get handled codes for this org
  const { data: handledCodes = [] } = useQuery({
    queryKey: ["handled-codes", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from("organisation_handling_relationships")
        .select("handled_code")
        .eq("organisation_id", organization.id)
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []).map((r) => r.handled_code);
    },
    enabled: !!organization?.id,
  });

  // 3. Fetch unified flights for today (PNG time) filtered by org + handled codes
  const { data: flights = [], isLoading: flightsLoading } = useQuery({
    queryKey: ["org-unified-flights", organization?.id, handledCodes],
    queryFn: async () => {
      if (!organization?.id) return [];

      // Build PNG-day boundaries
      const { data: timeData } = await supabase.rpc("now" as any);
      // Use JS approach: compute PNG day start/end
      const now = new Date();
      const pngNow = new Date(
        now.toLocaleString("en-US", { timeZone: "Pacific/Port_Moresby" })
      );
      const dayStart = new Date(pngNow);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      // Convert back to UTC ISO for query
      const pngOffsetMs = pngNow.getTime() - now.getTime();
      const gteUtc = new Date(dayStart.getTime() - pngOffsetMs).toISOString();
      const ltUtc = new Date(dayEnd.getTime() - pngOffsetMs).toISOString();

      // Fetch flights + lookup data in parallel
      const orgIdText = organization.id;

      // We need all flights where organization_id matches OR flight_number prefix matches handled codes
      const [flightsResult, orgsResult, citiesResult, aircraftResult] =
        await Promise.all([
          supabase
            .from("unified_flights" as any)
            .select("*")
            .gte("dep_datetime", gteUtc)
            .lt("dep_datetime", ltUtc)
            .order("dep_datetime", { ascending: true }),
          supabase.from("organizations").select("id, name, code"),
          supabase.from("cities").select("id, name, code"),
          supabase.from("aircraft_types").select("id, name, code"),
        ]);

      if (flightsResult.error) throw flightsResult.error;

      const rows = (flightsResult.data ?? []) as any[];

      // Filter: org_id matches OR flight prefix matches a handled code
      const filtered = rows.filter((row: any) => {
        if (row.organization_id === orgIdText) return true;
        // Check handled codes
        if (handledCodes.length > 0) {
          const prefix = (row.flight_number || "").replace(/[0-9].*/g, "");
          return handledCodes.includes(prefix);
        }
        return false;
      });

      // Build lookup maps
      const orgMap = new Map<string, any>();
      (orgsResult.data ?? []).forEach((o: any) => orgMap.set(o.id, o));
      const cityMap = new Map<string, any>();
      (citiesResult.data ?? []).forEach((c: any) => cityMap.set(c.id, c));
      const aircraftMap = new Map<string, any>();
      (aircraftResult.data ?? []).forEach((a: any) => aircraftMap.set(a.id, a));

      return filtered.map((row: any): Flight => {
        const org = orgMap.get(row.organization_id);
        const depCity = cityMap.get(row.departure_city_id);
        const arrCity = cityMap.get(row.arrival_city_id);
        const aircraft = row.aircraft_type_id
          ? aircraftMap.get(row.aircraft_type_id)
          : undefined;

        return {
          id: row.id,
          flight_number: row.flight_number,
          scheduled_departure: row.dep_datetime,
          scheduled_arrival: row.arr_datetime ?? row.dep_datetime,
          status: row.status as Flight["status"],
          gate: row.gate ?? undefined,
          organization_id: row.organization_id,
          departure_city_id: row.departure_city_id,
          arrival_city_id: row.arrival_city_id,
          aircraft_type_id: row.aircraft_type_id ?? "",
          created_at: "",
          updated_at: "",
          created_by: "",
          updated_by: "",
          organization: org
            ? { id: org.id, name: org.name, code: org.code }
            : undefined,
          departure_city: depCity
            ? { id: depCity.id, name: depCity.name, code: depCity.code }
            : undefined,
          arrival_city: arrCity
            ? { id: arrCity.id, name: arrCity.name, code: arrCity.code }
            : undefined,
          aircraft_type: aircraft
            ? { id: aircraft.id, name: aircraft.name, code: aircraft.code }
            : undefined,
        };
      });
    },
    enabled: !!organization?.id,
    staleTime: 30000,
  });

  // 4. Realtime subscription — re-fetch on UPDATE to flights for this org
  useEffect(() => {
    if (!organization?.id) return;

    const channel = supabase
      .channel(`org-flights-${organization.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "flights",
          filter: `organization_id=eq.${organization.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["org-unified-flights", organization.id, handledCodes],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organization?.id, handledCodes, queryClient]);

  useEffect(() => {
    const flightParam = searchParams.get("flight");
    if (flightParam && flights.length > 0 && !selectedFlight) {
      const match = flights.find(f => f.flight_number === flightParam);
      if (match) setSelectedFlight(match);
    }
  }, [searchParams, flights, selectedFlight]);

  const handleFlightClick = useCallback((flight: Flight) => {
    setSelectedFlight(flight);
    setSearchParams({ flight: flight.flight_number }, { replace: true });
  }, [setSearchParams]);

  const handleClosePanel = useCallback(() => {
    setSelectedFlight(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  if (orgLoading || flightsLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#0a0f2c" }}
      >
        <Loader2
          className="h-8 w-8 animate-spin"
          style={{ color: "#00c8d7" }}
        />
        <span className="ml-2 text-lg text-white">Loading flights...</span>
      </div>
    );
  }

  if (!organization) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#0a0f2c" }}
      >
        <p className="text-white text-lg">
          No flights found for this airline
        </p>
      </div>
    );
  }

  const pageTitle = `${organization.name} — Today's Flights`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={`View ${organization.name} flight schedules. Real-time status updates.`}
        keywords={`${organization.name}, ${organization.code}, flights, Papua New Guinea`}
      />

      <div className="min-h-screen" style={{ backgroundColor: "#0a0f2c" }}>
        <FIDSHeader title={pageTitle} />

        <div className="w-full">
          <AnnouncementBanner />

          {flights.length === 0 ? (
            <div className="p-12 text-center text-white">
              <p className="text-lg">No flights scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-0">
              <FIDSFlightBoard flights={flights} mode="all" onFlightClick={handleFlightClick} />

              <div className="px-4 py-6 text-center text-white/70 [&_a]:text-[#00c8d7] [&_a:hover]:text-[#00c8d7]/80 [&_p]:text-white/70 [&_.border-t]:border-white/20">
                <OrganizationLinks />
              </div>
            </div>
          )}
        </div>

        <FIDSMarquee />
        <FlightDetailPanel flight={selectedFlight} onClose={handleClosePanel} />
      </div>
    </>
  );
}
